// AppLayout — the persistent shell for every authenticated CRM route.
//
// Grid:
//   +----------+--------------------------------+--------------+
//   | Sidebar  | Topbar                         |              |
//   |          +--------------------------------+  ChatPanel   |
//   |          | <Outlet /> (router children)   |  (stub)      |
//   +----------+--------------------------------+--------------+
//
// Responsibilities:
//   - Gate everything on AuthGuard (redirects to /login if no session).
//   - Emit `screen_view` via usePageView() on every route change.
//   - Render a placeholder ChatPanel slot gated by the `ai-chat` flag. The
//     real `@govard/ai-chat-kit` panel is wired in Week 4.
//
// Ported structurally from erp2 `components/layout/AppLayout.tsx`, preserving
// shell shape (Sidebar + Topbar + TabBar + RightSidebar) while stripping
// GovardOS/Electron-only bindings.
//
// TODO [WEEK-4]: port CommandPalette (Cmd+K) as an optional overlay + wire
// the real ChatPanel from @govard/ai-chat-kit.

import { useEffect, useState, type ReactElement } from 'react';
import { ConfigProvider } from 'antd';
import { govardTheme } from '../../theme/antdTheme.js';
import { Outlet, useLocation } from 'react-router-dom';

import { useAnalytics, usePageView } from '@govard/analytics';
import { useTranslation } from '@govard/i18n-core/react';
import { useFeatureFlag } from '@govard/feature-flags/react';

import { AuthGuard } from '../../shared/auth/AuthGuard.js';
import { CRM_FLAG_IDS } from '../../feature-flags/index.js';
import { RightSidebar } from './RightSidebar.js';
import { Sidebar } from './Sidebar.js';
import { TabBar } from './TabBar.js';
import { Topbar } from './Topbar.js';

function AppShell(): ReactElement {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const location = useLocation();
  const aiChat = useFeatureFlag(CRM_FLAG_IDS.AI_CHAT);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const shouldShowAiSlot = aiChat && isAiContextRoute(location.pathname);
  const shouldShowContextSidebar = rightSidebarOpen && !shouldShowAiSlot;

  // Fire once on the very first paint (usePageView handles `window` guards).
  usePageView();

  // Fire on subsequent pathname changes — react-router does NOT trigger a full
  // remount, so usePageView's `useEffect([analytics])` alone would miss them.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    analytics.track('screen_view', {
      screen: location.pathname,
      product: 'crm',
      lang: 'ru',
    });
  }, [analytics, location.pathname]);

  return (
    <ConfigProvider theme={govardTheme}>
      <div className="flex h-screen w-screen overflow-hidden" style={{ background: 'var(--ant-color-bg-layout, #f9f7f4)', color: 'var(--ant-color-text, #44403c)' }}>
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar
            rightSidebarOpen={shouldShowContextSidebar}
            onToggleRightSidebar={() => {
              setRightSidebarOpen((value) => !value);
            }}
          />
          <TabBar />
          <div className="flex min-h-0 flex-1">
            <main
              className="min-w-0 flex-1 flex flex-col overflow-hidden bg-background"
              role="main"
              aria-label={t('crm.sidebar.brand')}
            >
              <div className={isEdgeToEdgeRoute(location.pathname) ? "h-full w-full overflow-y-auto flex flex-col" : "h-full w-full overflow-y-auto p-6 lg:px-6 lg:py-6"}>
                <div className={isEdgeToEdgeRoute(location.pathname) ? "flex-1 flex flex-col min-h-0" : "max-w-[1600px] mx-auto"}>
                  <Outlet />
                </div>
              </div>
            </main>
            <RightSidebar
              isOpen={shouldShowContextSidebar}
              onClose={() => {
                setRightSidebarOpen(false);
              }}
            />
          </div>
        </div>
        {shouldShowAiSlot ? (
          <aside
            aria-label="AI chat"
            data-testid="crm-chat-panel-slot"
            className="hidden w-80 shrink-0 border-l border-border bg-muted/30 lg:flex lg:flex-col"
          >
            {/* TODO [WEEK-4]: mount @govard/ai-chat-kit panel with real scope +
              provider. For now this is an empty slot so the shell reserves
              the column width. */}
          </aside>
        ) : null}
      </div>
    </ConfigProvider>
  );
}

function isAiContextRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/deals/') || pathname.startsWith('/contacts/') || pathname === '/hot-leads'
  );
}

function isEdgeToEdgeRoute(pathname: string): boolean {
  return pathname.startsWith('/deals/d-');
}

export function AppLayout(): ReactElement {
  return (
    <AuthGuard>
      <AppShell />
    </AuthGuard>
  );
}
